import { useState } from "react";
import {
  FolderKanban,
  Edit3,
  Calendar,
  Users,
  Globe,
  Building,
  Trash2,
  Plus,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useDeleteProject } from "./hooks/useProjects";
import { useCandidateProjects } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { ProjectFormModal } from "./components/ProjectFormModal";
import type { ProjectDetail } from "@/interface/profile";

export default function ProjectsTab({
  profileId,
}: {
  profileId?: number | string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectDetail | null>(
    null,
  );
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const { data: user } = useCurrentUser();
  const targetId = profileId || user?.id;

  const {
    data: candProjectsRes,
    isLoading: isCandLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateProjects(targetId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteProject();

  const projects =
    candProjectsRes?.pages.flatMap((page: any) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const isLoading = isCandLoading && projects?.length === 0;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleOpenAdd = () => {
    setSelectedProject(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (project: ProjectDetail) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="space-y-6 flex flex-col">
      {/* Header & Add Button */}
      <div className="flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Projects
            Portfolio
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage your project history and key contributions.
          </p>
        </div>
        <Button
          onClick={handleOpenAdd}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {/* Projects List */}
      {isLoading && projects.length === 0 ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto space-y-8 max-h-[700px] flex-1 custom-scrollbar"
        >
          <div className="grid grid-cols-1 gap-8">
            {projects?.map((project: ProjectDetail) => (
              <div
                key={project.id}
                className="bg-white dark:bg-slate-900 rounded-2xl p-8 lg:p-10 shadow-sm border border-slate-200 dark:border-slate-800 group relative"
              >
                {/* Action Buttons */}
                <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button
                    onClick={() => handleOpenEdit(project)}
                    className="w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-200 dark:hover:border-purple-800 flex items-center justify-center transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTargetId(String(project.id))}
                    className="w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800 flex items-center justify-center transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-10">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <h3 className="text-xl font-bold text-purple-900 dark:text-purple-300">
                        {project.project_name}
                      </h3>
                      {project.is_current && (
                        <span className="bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/50 dark:border-emerald-800/50 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                          Current
                        </span>
                      )}
                    </div>

                    {project.role && (
                      <p className="text-[15px] font-bold text-slate-700 dark:text-slate-200 mb-6">
                        {project.role}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500 dark:text-slate-400 mb-8">
                      {project.client_organization && (
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4" />{" "}
                          {project.client_organization}
                        </div>
                      )}
                      {project.industry?.industry_name && (
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />{" "}
                          {project.industry?.industry_name}
                        </div>
                      )}
                      {(project.start_date || project.end_date) && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> {project.start_date}{" "}
                          - {project.is_current ? "Present" : project.end_date}
                        </div>
                      )}
                      {project.team_size !== null &&
                        project.team_size !== undefined && (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" /> Team Size:{" "}
                            {project.team_size}
                          </div>
                        )}
                    </div>

                    <div className="space-y-6 text-[15px] text-slate-700 dark:text-slate-300">
                      {project.description && (
                        <div>
                          <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                            Description
                          </h4>
                          <p className="leading-relaxed max-w-4xl whitespace-pre-line">
                            {project.description}
                          </p>
                        </div>
                      )}

                      {project.key_responsibilities && (
                        <div>
                          <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                            Key Responsibilities
                          </h4>
                          <p className="leading-relaxed max-w-4xl whitespace-pre-line">
                            {project.key_responsibilities}
                          </p>
                        </div>
                      )}

                      {(project.contributions || project.achievements) && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
                          {project.contributions && (
                            <div className="bg-slate-50 dark:bg-slate-800/60 p-5 rounded-xl border border-slate-100 dark:border-slate-800">
                              <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-xs uppercase tracking-wider">
                                Contributions
                              </h4>
                              <p className="text-[14px] leading-relaxed whitespace-pre-line">
                                {project.contributions}
                              </p>
                            </div>
                          )}
                          {project.achievements && (
                            <div className="bg-amber-50/50 dark:bg-amber-950/20 p-5 rounded-xl border border-amber-100 dark:border-amber-900/40">
                              <h4 className="font-semibold text-amber-900 dark:text-amber-300 mb-2 text-xs uppercase tracking-wider">
                                Achievements
                              </h4>
                              <p className="text-[14px] leading-relaxed text-amber-800 dark:text-amber-200 whitespace-pre-line">
                                {project.achievements}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Technologies Column */}
                  <div className="lg:w-72 shrink-0 lg:border-l border-slate-100 dark:border-slate-800 lg:pl-10 pt-6 lg:pt-0">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm uppercase tracking-wide">
                      Technologies Used
                    </h4>
                    <div className="flex flex-wrap gap-2.5">
                      {project.technologies &&
                        project.technologies.length > 0 ? (
                        project.technologies.map(
                          (
                            tech:
                              | number
                              | string
                              | { id?: number; skill_name?: string },
                            idx: number,
                          ) => {
                            const name =
                              typeof tech === "object"
                                ? tech.skill_name
                                : String(tech);
                            return (
                              <span
                                key={
                                  typeof tech === "object"
                                    ? tech.id || idx
                                    : tech
                                }
                                className="bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800/50 text-[13px] font-medium px-3 py-1.5 rounded-lg"
                              >
                                {name}
                              </span>
                            );
                          },
                        )
                      ) : (
                        <span className="text-xs text-slate-400 italic">
                          No technologies listed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {projects.length === 0 && (
              <div className="text-center p-12 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900">
                No project records added yet.
              </div>
            )}
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-500 font-medium">
                Loading more projects...
              </span>
            </div>
          )}
        </div>
      )}

      <ProjectFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedProject}
        profileId={targetId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Project"
        description="Are you sure you want to remove this project?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
